import { useState } from 'react';
import { Dropdown, Form } from 'react-bootstrap';

export type MultiSelectOption = {
  value: string;
  label: string;
};

export type MultiSelectGroup = {
  label: string;
  options?: readonly MultiSelectOption[];
  groups?: readonly MultiSelectGroup[];
};

type MultiSelectFilterProps = {
  name: string;
  label: string;
  options?: readonly MultiSelectOption[];
  groups?: readonly MultiSelectGroup[];
  selected: readonly string[];
  onChange: (selected: string[]) => void;
  // Given, the control reads as a named select rather than a funnel icon: a filter that is not
  // sitting under a column heading has nothing else to say what it filters.
  summaryOf?: (selected: readonly string[]) => string;
};

const controlName = (label: string, selectedCount: number): string =>
  selectedCount === 0 ? `Filter by ${label}` : `Filter by ${label}, ${selectedCount} selected`;

// The visible text is part of the accessible name, so a reader hears what the control says.
const namedControlName = (label: string, summary: string): string => `Filter by ${label}: ${summary}`;

const sameValues = (left: readonly string[], right: readonly string[]): boolean =>
  left.length === right.length && left.every((value) => right.includes(value));

// Popper writes its own `transform` unless gpu acceleration is off, and an animation on `transform`
// overrides that inline style while it runs — the menu would open in the wrong place. Positioning by
// `top`/`left` leaves `transform` free for the open animation.
const POPPER_CONFIG = {
  strategy: 'fixed' as const,
  modifiers: [{ name: 'computeStyles', options: { gpuAcceleration: false } }],
};

function FunnelIcon() {
  return (
    <svg viewBox="0 0 16 16" width="11" height="11" aria-hidden="true" focusable="false">
      <path
        d="M1.6 2h12.8a.5.5 0 0 1 .38.83L10 8.63v4.02a.5.5 0 0 1-.72.45l-2.4-1.2a.5.5 0 0 1-.28-.45V8.63L1.22 2.83A.5.5 0 0 1 1.6 2Z"
        fill="currentColor"
      />
    </svg>
  );
}

function CaretIcon() {
  return (
    <svg viewBox="0 0 16 16" width="10" height="10" aria-hidden="true" focusable="false">
      <path d="M3.5 6h9L8 11z" fill="currentColor" />
    </svg>
  );
}

type OptionChecksProps = {
  name: string;
  options: readonly MultiSelectOption[];
  draft: readonly string[];
  onTick: (value: string, checked: boolean) => void;
};

function OptionChecks({ name, options, draft, onTick }: OptionChecksProps) {
  return (
    <>
      {options.map((option) => (
        <Form.Check
          key={option.value}
          id={`${name}-${option.value}`}
          type="checkbox"
          label={option.label}
          checked={draft.includes(option.value)}
          onChange={(event) => onTick(option.value, event.target.checked)}
        />
      ))}
    </>
  );
}

type OptionGroupsProps = {
  name: string;
  groups: readonly MultiSelectGroup[];
  path: string;
  draft: readonly string[];
  onTick: (value: string, checked: boolean) => void;
};

function OptionGroups({ name, groups, path, draft, onTick }: OptionGroupsProps) {
  return (
    <>
      {groups.map((group, index) => {
        const groupPath = `${path}-${index}`;
        const headingId = `${groupPath}-heading`;

        return (
          <div key={group.label} role="group" aria-labelledby={headingId} className="diagnostics-filter-group">
            <div id={headingId} className="diagnostics-filter-group-heading">
              {group.label}
            </div>
            <OptionChecks name={name} options={group.options ?? []} draft={draft} onTick={onTick} />
            <OptionGroups name={name} groups={group.groups ?? []} path={groupPath} draft={draft} onTick={onTick} />
          </div>
        );
      })}
    </>
  );
}

function MultiSelectFilter({ name, label, options = [], groups, selected, onChange, summaryOf }: MultiSelectFilterProps) {
  const [shown, setShown] = useState(false);
  const [draft, setDraft] = useState<readonly string[]>(selected);

  const open = (): void => {
    setDraft(selected);
    setShown(true);
  };

  const close = (): void => {
    setShown(false);
    if (!sameValues(draft, selected)) onChange([...draft]);
  };

  const tick = (value: string, checked: boolean): void => {
    setDraft(checked ? [...draft, value] : draft.filter((each) => each !== value));
  };

  return (
    <Dropdown
      autoClose="outside"
      className="diagnostics-column-filter"
      align="end"
      show={shown}
      onToggle={(nextShown) => (nextShown ? open() : close())}
    >
      <Dropdown.Toggle
        as="button"
        type="button"
        bsPrefix="diagnostics-filter-toggle"
        className={[summaryOf === undefined ? undefined : 'is-named', selected.length === 0 ? undefined : 'is-active']
          .filter(Boolean)
          .join(' ')}
        aria-label={
          summaryOf === undefined
            ? controlName(label, selected.length)
            : namedControlName(label, summaryOf(selected))
        }
      >
        {summaryOf === undefined ? <FunnelIcon /> : summaryOf(selected)}
        {summaryOf !== undefined && <CaretIcon />}
      </Dropdown.Toggle>
      {/* Not `className`: `bsPrefix` replaces the `dropdown-menu` class rather than adding to it.
          The admin shell loads Bootstrap's own dropdown JS (public/js/vendor.bundle.js), which
          delegates a keydown handler on document, in the capture phase, for anything inside a
          `.dropdown-menu`. It then looks for a sibling `[data-bs-toggle="dropdown"]` that
          react-bootstrap never renders, and throws on Escape and the arrow keys. Capture at
          document outranks every listener we could add, so the menu must not match the selector.
          The styles this loses are in diagnostics.css. */}
      <Dropdown.Menu bsPrefix="diagnostics-filter-menu" popperConfig={POPPER_CONFIG}>
        <div className="diagnostics-filter-options">
          {groups === undefined ? (
            <OptionChecks name={name} options={options} draft={draft} onTick={tick} />
          ) : (
            <OptionGroups name={name} groups={groups} path={name} draft={draft} onTick={tick} />
          )}
        </div>
        <div className="diagnostics-filter-footer">
          <button
            type="button"
            className="diagnostics-filter-reset"
            disabled={draft.length === 0}
            onClick={() => setDraft([])}
          >
            Reset
          </button>
          <button type="button" className="diagnostics-filter-ok" onClick={close}>
            OK
          </button>
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
}

export default MultiSelectFilter;
